import { useMemo, useState } from 'react';

import { StatTile } from '@/components/shell/StatTile';
import { DataTable, DataTableCell, DataTableHead } from '@/components/table/DataTable';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';
import { Sheet } from '@/components/ui/sheet';
import { AI_WINDOWS, type AiKey, type AiKeyOwner, type AiKeyStatus, type AiRoute, type AiWindow, aiApi, num, windowRange } from '@/lib/aiAdmin';
import { formatCount, formatDateTime, formatUsd } from '@/lib/format';
import { formatAge } from '@/lib/microvm';
import { cn } from '@/lib/utils';

import { CENTER, columns, Loading, LoadFailed, SectionLabel, useLoad } from './common';

const WINDOW_OPTIONS = AI_WINDOWS.map((id) => ({ id, label: { '24h': '24 hours', '7d': '7 days', '30d': '30 days' }[id] }));
const ALL = 'all';

const OWNER_LABEL: Record<AiKeyOwner, string> = { KEY_OWNER_COMPANY: 'Company', KEY_OWNER_ORG: 'Organization', KEY_OWNER_APP: 'App', KEY_OWNER_PERSON: 'Person' };
const KEY_SOURCE: Partial<Record<AiKeyOwner, 'company' | 'org' | 'app'>> = { KEY_OWNER_COMPANY: 'company', KEY_OWNER_ORG: 'org', KEY_OWNER_APP: 'app' };
const STORE_LABEL: Record<string, string> = { KEY_STORE_AWS_SECRETS_MANAGER: 'AWS Secrets Manager', KEY_STORE_AWS_IAM_ROLE: 'AWS IAM role', KEY_STORE_VAULT: 'RapidSOS vault' };
const STATUS: Record<AiKeyStatus, { text: string; tone: string; note?: string }> = {
  KEY_STATUS_WORKING: { text: 'Working', tone: 'text-success' },
  KEY_STATUS_ERRORS: { text: 'Errors', tone: 'text-warning' },
  KEY_STATUS_FAILING: { text: 'Failing', tone: 'text-destructive', note: 'The provider refused the key' },
  KEY_STATUS_NO_CALLS: { text: 'No calls', tone: 'text-muted-foreground' },
  KEY_STATUS_OFF: { text: 'Off', tone: 'text-muted-foreground' },
};
// `ai` leaves out enum value 0, so a missing status is Unknown.
const statusOf = (key: AiKey) => (key.status && STATUS[key.status]) || { text: 'Unknown', tone: 'text-muted-foreground', note: undefined };

const isCompany = (key: AiKey) => key.owner === 'KEY_OWNER_COMPANY';
const costOf = (key: AiKey) => num(key.costUsd);
const money = (amount: number) => (amount === 0 ? '$0.00' : formatUsd(amount));
const plural = (count: number, word: string) => `${formatCount(count)} ${word}${count === 1 ? '' : 's'}`;
const providerLabel = (id: string) => id.charAt(0).toUpperCase() + id.slice(1);
// A usage day key is YYYY-MM-DD in UTC.
const dayLabel = (day: string) => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${day}T00:00:00Z`));
const lastUsed = (key: AiKey) => (key.lastUsedAt ? `${formatAge(key.lastUsedAt, Date.now())} ago` : 'Never');

/** An own key shows its organization, and its app key when it has one. */
const subtitle = (key: AiKey) => (isCompany(key) ? undefined : [key.organizationName || key.organizationId, key.appKey].filter(Boolean).join(' / ') || undefined);

type Props = { routes: AiRoute[] | undefined; routesFailed?: boolean };

function Detail({ keyRow, routes, routesFailed, range, onClose }: Props & { keyRow: AiKey; range: { from: string; to: string }; onClose: () => void }) {
  const keySource = keyRow.owner ? KEY_SOURCE[keyRow.owner] : undefined;
  // ai filters by the whole key, so the panel shows this key's calls only. Without a known key source it asks for nothing.
  const scope = keySource && {
    provider: keyRow.provider,
    keySource,
    ...(isCompany(keyRow) ? {} : { organizationId: keyRow.organizationId }),
    ...(keySource === 'app' ? { appKey: keyRow.appKey } : {}),
  };
  // In ai's order: grouped by model, each model's routes in priority order.
  const keyRoutes = (keyRow.routeIds ?? []).flatMap((id) => routes?.find((route) => route.id === id) ?? []);
  const days = useLoad(() => (scope ? aiApi.usage(range.from, range.to, 'day', scope) : Promise.resolve({ rows: [] })), [keyRow.keyId, range]);
  const failed = useLoad(
    () => (scope ? aiApi.calls({ ...scope, status: 'failed', from: range.from, to: range.to, pageSize: '5' }) : Promise.resolve({ calls: [] })),
    [keyRow.keyId, range],
  );
  const failedCalls = failed.data?.calls ?? [];
  return (
    <Sheet open onClose={onClose} title={keyRow.label || keyRow.keyId} description={subtitle(keyRow) ?? `${OWNER_LABEL[keyRow.owner ?? 'KEY_OWNER_COMPANY']} key, ${keyRow.provider ? providerLabel(keyRow.provider) : 'unknown provider'}`}>
      <div className="space-y-6 text-sm">
        <section>
          <SectionLabel>Routes</SectionLabel>
          {routesFailed ? (
            <p className="text-muted-foreground">The routes could not be loaded.</p>
          ) : !routes ? (
            <Loading label="Loading routes" />
          ) : keyRoutes.length === 0 ? (
            <p className="text-muted-foreground">No routes.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {keyRoutes.map((route) => (
                <li key={route.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <span className="min-w-0 truncate">{route.id}</span>
                  <span className={route.enabled === true ? 'text-success' : 'text-muted-foreground'}>{route.enabled === true ? 'On' : 'Off'}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <SectionLabel>Calls by day</SectionLabel>
          {days.error ? (
            <LoadFailed message={days.error} onRetry={days.reload} />
          ) : !days.data ? (
            <Loading label="Loading calls by day" />
          ) : (days.data.rows ?? []).length === 0 ? (
            <p className="text-muted-foreground">No calls in this window.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {(days.data.rows ?? []).map((row) => (
                <li key={row.key} className="flex justify-between gap-3 px-3 py-2 tabular-nums">
                  <span>{row.key ? dayLabel(row.key) : '—'}</span>
                  <span>
                    {plural(num(row.calls), 'call')}
                    {num(row.errors) > 0 ? `, ${plural(num(row.errors), 'error')}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <SectionLabel>Last failed calls</SectionLabel>
          {failed.error ? (
            <LoadFailed message={failed.error} onRetry={failed.reload} />
          ) : !failed.data ? (
            <Loading label="Loading failed calls" />
          ) : failedCalls.length === 0 ? (
            <p className="text-muted-foreground">No failed calls.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {failedCalls.slice(0, 5).map((call) => (
                <li key={call.id} className="px-3 py-2">
                  <span className="block">{formatDateTime(call.createdAt)}</span>
                  <span className="block text-xs text-muted-foreground">
                    {call.routeId || '—'} {call.reason || ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Sheet>
  );
}

/** Every key the `ai` service can use, its status and its use. Facts about keys only: no value ever reaches this page. */
export function KeysTab({ routes, routesFailed }: Props) {
  const [timeWindow, setTimeWindow] = useState<AiWindow>('30d');
  const [provider, setProvider] = useState(ALL);
  const [owner, setOwner] = useState(ALL);
  const [selected, setSelected] = useState<string | undefined>();
  const range = useMemo(() => windowRange(timeWindow), [timeWindow]);
  const load = useLoad(() => aiApi.keys(range.from, range.to), [range]);
  const keys = load.data?.keys ?? [];

  const company = keys.filter(isCompany);
  const failing = keys.filter((key) => key.status === 'KEY_STATUS_FAILING').length;
  const errors = keys.filter((key) => key.status === 'KEY_STATUS_ERRORS').length;
  const spend = (list: AiKey[]) => list.reduce((sum, key) => sum + costOf(key), 0);
  const own = keys.filter((key) => !isCompany(key));

  const providers = [...new Set(keys.map((key) => key.provider).filter((value): value is string => Boolean(value)))].sort();
  const owners = [...new Set(keys.map((key) => key.owner).filter((value): value is AiKeyOwner => Boolean(value)))];
  const shown = keys.filter((key) => (provider === ALL || key.provider === provider) && (owner === ALL || key.owner === owner));
  const open = keys.find((key) => key.keyId === selected);

  return (
    <div className="space-y-4">
      <SectionLabel actions={<SegmentedFilter label="Time window" options={WINDOW_OPTIONS} value={timeWindow} onChange={setTimeWindow} />}>Keys</SectionLabel>
      {load.error ? (
        <LoadFailed message={load.error} onRetry={load.reload} />
      ) : !load.data ? (
        <Loading label="Loading keys" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            <StatTile label="Keys" value={formatCount(keys.length)} detail={`${company.length} company, ${own.length} own`} />
            <StatTile label="Used" value={formatCount(keys.filter((key) => num(key.calls) > 0).length)} detail={`with calls in ${WINDOW_OPTIONS.find((o) => o.id === timeWindow)?.label}`} />
            <StatTile label="Failing" value={formatCount(failing + errors)} detail={`${formatCount(failing)} refused, ${plural(errors, 'error')}`} />
            <StatTile label="Spend" value={money(spend(keys))} detail={`company ${money(spend(company))}, own ${money(spend(own))}`} />
          </div>
          <DataTable
            toolbar={
              <>
                <SegmentedFilter label="Provider" options={[{ id: ALL, label: 'All' }, ...providers.map((id) => ({ id, label: providerLabel(id) }))]} value={provider} onChange={setProvider} />
                <SegmentedFilter label="Owner" options={[{ id: ALL, label: 'All' }, ...owners.map((id) => ({ id: id as string, label: OWNER_LABEL[id] }))]} value={owner} onChange={setOwner} />
              </>
            }
          >
            <DataTableHead columns={columns(['Key', 'Owner', 'Stored in', 'Status', 'Last used', 'Calls', 'Cost'])} />
            <tbody>
              {shown.map((key) => {
                const status = statusOf(key);
                const sub = subtitle(key);
                return (
                  <tr key={key.keyId} onClick={() => setSelected(key.keyId)} className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/40">
                    <DataTableCell className="px-3 font-medium">
                      {/* The row's click handler opens the detail; the button makes it reachable by keyboard. */}
                      <button type="button" className="cursor-pointer text-left font-medium">
                        {key.label || key.keyId}
                      </button>
                      {sub ? <span className="mt-0.5 block max-w-[12rem] truncate text-xs font-normal text-muted-foreground">{sub}</span> : null}
                    </DataTableCell>
                    <DataTableCell className={cn(CENTER, 'px-3')}>{key.owner ? OWNER_LABEL[key.owner] : '—'}</DataTableCell>
                    <DataTableCell className="px-3 text-center">{(key.storedIn && STORE_LABEL[key.storedIn]) || '—'}</DataTableCell>
                    <DataTableCell className={cn(CENTER, 'px-3', status.tone)}>
                      {status.text}
                      {status.note ? <span className="mx-auto mt-0.5 block max-w-[9rem] whitespace-normal text-xs text-muted-foreground">{status.note}</span> : null}
                    </DataTableCell>
                    <DataTableCell className={cn(CENTER, 'px-3')}>{lastUsed(key)}</DataTableCell>
                    <DataTableCell className={cn(CENTER, 'px-3 tabular-nums')}>{formatCount(num(key.calls))}</DataTableCell>
                    <DataTableCell className={cn(CENTER, 'px-3 tabular-nums')}>{money(costOf(key))}</DataTableCell>
                  </tr>
                );
              })}
              {shown.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    No keys match these filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </DataTable>
          {own.length === 0 ? <p className="text-xs text-muted-foreground">No own keys yet. Organizations and people can add their own keys soon.</p> : null}
        </>
      )}
      {open ? <Detail key={open.keyId} keyRow={open} routes={routes} routesFailed={routesFailed} range={range} onClose={() => setSelected(undefined)} /> : null}
    </div>
  );
}
